import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { eisfSigning } from '@/api/client';

/**
 * Self-heal against DocuSeal directly for a single version, independent of
 * whether anyone has the in-app sign dialog open. The DocuSeal webhook is
 * the fast path for flipping a version from in_approval -> current, but it
 * can silently go stale (see services/eisf/signatureSync.ts) — an approver
 * who signs via the EMAILED link never opens the in-app sign iframe, so
 * without this, that signature only ever lands if the webhook happens to
 * still work.
 *
 * Polls DocuSeal every 15s while `enabled`; if it finds a completion our DB
 * doesn't know about yet, calls the same finalize applier the webhook would
 * call, then invalidates the queries that display approval/version state.
 * Safe to mount many instances at once (e.g. one per in_approval document
 * in a folder list) — each is scoped to its own versionId.
 */
export function useDocusealSelfHeal(versionId: string, enabled: boolean): void {
  const qc = useQueryClient();
  const finalizeTriggeredRef = useRef(false);

  const syncStatusQ = useQuery({
    queryKey: ['eisf', 'sign-sync-status', versionId],
    queryFn: () => eisfSigning.syncStatus(versionId),
    refetchInterval: 15_000,
    enabled,
  });

  const finalizeMut = useMutation({
    mutationFn: () => eisfSigning.finalizeSync(versionId),
    onSuccess: (r) => {
      if (r.synced_count > 0) {
        qc.invalidateQueries({ queryKey: ['approval-routing'] });
        qc.invalidateQueries({ queryKey: ['document-preview'] });
        qc.invalidateQueries({ queryKey: ['binder'] });
      }
    },
  });

  useEffect(() => {
    if (!enabled) return;
    if (!syncStatusQ.data?.any_newly_completed) return;
    if (finalizeTriggeredRef.current || finalizeMut.isPending) return;
    finalizeTriggeredRef.current = true;
    finalizeMut.mutate(undefined, {
      onSettled: () => { finalizeTriggeredRef.current = false; },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, syncStatusQ.data]);
}
